import React, { useEffect, useState } from 'react';
import { FeatureListItem } from './FeatureListItem';
import { InteractiveDashboardPreview } from './InteractiveDashboardPreview';
import { splitScreenFeatures } from '../../data/splitScreenFeatures';
export interface FeatureSectionProps {
  'data-id'?: string;
}
/**
 * FeatureSection component
 * Split-screen interactive demo showing features and live dashboard
 * Left: Clickable feature list
 * Right: Dashboard preview that updates based on selection
 */
export const FeatureSection: React.FC<FeatureSectionProps> = ({
  'data-id': dataId
}) => {
  const [activeFeature, setActiveFeature] = useState<number>(0);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      });
    }, {
      threshold: 0.1
    });
    const section = document.getElementById('dashboard-features-section');
    if (section) {
      observer.observe(section);
    }
    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);
  return <section id="dashboard-features-section" className="relative w-full bg-gradient-to-b from-[#070707] via-[#0a0a0a] to-[#070707] px-6 sm:px-8 lg:px-16 py-20 overflow-hidden" data-id="element-270">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-20" data-id="element-271">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#3ab795] rounded-full blur-[120px] opacity-20" style={{
        animation: 'float 8s ease-in-out infinite'
      }} data-id="element-272" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#3ab795] rounded-full blur-[120px] opacity-20" style={{
        animation: 'float 10s ease-in-out infinite 1s'
      }} data-id="element-273" />
      </div>

      {/* Section Header */}
      <div className={`relative z-10 flex flex-col items-center gap-8 mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-274">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#3ab795]/10 border border-[#3ab795]/20 rounded-full backdrop-blur-sm" data-id="element-275">
          <div className="w-2 h-2 rounded-full bg-[#3ab795] animate-pulse" data-id="element-276" />
          <p className="text-sm md:text-base font-medium text-[#3ab795] tracking-wide uppercase" data-id="element-277">
            Interactive Demo
          </p>
        </div>

        {/* Title and Description */}
        <div className="flex flex-col gap-6 items-center text-center w-full max-w-4xl" data-id="element-278">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight" style={{
          fontFamily: '"Clash Grotesk", "Clash Grotesk Placeholder", sans-serif'
        }} data-id="element-279">
            Your Dashboard for{' '}
            <span className="relative inline-block" data-id="element-280">
              <span className="relative z-10 bg-gradient-to-r from-[#3ab795] to-[#2d9f7e] bg-clip-text text-transparent" data-id="element-281">
                Smarter Decisions
              </span>
              <span className="absolute bottom-0 left-0 w-full h-3 bg-[#3ab795]/20 blur-lg -z-10" data-id="element-282" />
            </span>
          </h2>
          <p className="text-xl md:text-2xl text-[#b8bcc8] leading-relaxed max-w-3xl" data-id="element-283">
            Click through features to see how our dashboard helps you{' '}
            <span className="text-white font-semibold" data-id="element-284">track deals</span>,{' '}
            <span className="text-white font-semibold" data-id="element-285">
              optimize performance
            </span>
            , and{' '}
            <span className="text-white font-semibold" data-id="element-286">
              close more opportunities
            </span>
            .
          </p>
        </div>
      </div>

      {/* Split-Screen Layout */}
      <div className="relative z-10 w-full max-w-[1400px] mx-auto" data-id="element-287">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12" data-id="element-288">
          {/* Left Side - Feature List */}
          <div className="lg:col-span-2 space-y-4" data-id="element-289">
            {splitScreenFeatures.map(feature => <FeatureListItem key={feature.id} icon={feature.icon} title={feature.title} description={feature.description} isActive={activeFeature === feature.id} onClick={() => setActiveFeature(feature.id)} data-id="element-290" />)}
          </div>

          {/* Right Side - Dashboard Preview */}
          <div className="lg:col-span-3" data-id="element-291">
            <InteractiveDashboardPreview activeFeature={activeFeature} data-id="element-292" />
          </div>
        </div>
      </div>

      {/* Bottom CTA */}
      <div className={`relative z-10 flex justify-center mt-16 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-293">
        <a href="#pricing" className="group inline-flex items-center gap-3 px-8 py-4 bg-[#3ab795] text-white font-semibold text-lg rounded-xl transition-all duration-300 hover:bg-[#2d9f7e] hover:scale-105 hover:shadow-[0_8px_24px_rgba(58,183,149,0.3)] active:scale-100 focus:outline-none focus:ring-2 focus:ring-[#3ab795] focus:ring-offset-2 focus:ring-offset-[#0a0a0a]" data-id="element-294">
          <span data-id="element-295">Start Free Trial</span>
          <svg className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-296">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" data-id="element-297" />
          </svg>
        </a>
      </div>
    </section>;
};